import { useEffect, useRef, useState } from 'react';
import { useCompany } from '@/company/context';
import { useSessionBoundFetch } from '@/lib/use-session-guard';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Segmented } from '@/components/ui/kit';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import deletionCopy from '@/i18n/ns/library-deletion';
import panelsCopy from '@/i18n/ns/artifact-panels';
import { useWorkspace } from '../context';
import { useIntlLocale } from '../intl-locale';
import { notifyLibraryChanged } from '../artifacts/library-changed';
import { itemArchiveApi, type ArchiveKind, type ArchivedItem } from './api';
import { archiveError } from './errors';
import './archive.css';

export function LibraryArchive() {
  const company = useCompany();
  const ws = useWorkspace();
  return <ScopedArchive key={`${company.data?.company?.id}:${ws.user?.id}`} />;
}

function ScopedArchive() {
  const ws = useWorkspace();
  const session = useRef(useSessionBoundFetch()).current;
  const copy = useNamespaceCopy(deletionCopy);
  const panels = useNamespaceCopy(panelsCopy);
  const locale = useIntlLocale();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<ArchiveKind>('file');
  const [items, setItems] = useState<ArchivedItem[]>([]);
  const [offset, setOffset] = useState(0);
  const [next, setNext] = useState<number | null>(null);
  const [reload, setReload] = useState(0);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [error, setError] = useState('');
  const busy = loading || restoring !== null;

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    setError('');
    void itemArchiveApi
      .list(kind, offset, session)
      .then((page) => {
        if (!active) return;
        setItems((previous) => (offset ? [...previous, ...page.items] : page.items));
        setNext(page.nextOffset);
      })
      .catch((failure) => {
        if (active) setError(archiveError(failure, copy));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, kind, offset, reload, copy, session]);

  function reset() {
    setItems([]);
    setNext(null);
    setOffset(0);
    setReload((value) => value + 1);
  }

  async function restore(id: string) {
    const current = ws.beginNavigation();
    setRestoring(id);
    setError('');
    try {
      await itemArchiveApi.restore(kind, id, session);
      if (!current()) return;
      reset();
      notifyLibraryChanged();
      ws.notify(copy.restored);
      await ws.refresh();
    } catch (failure) {
      if (current()) setError(archiveError(failure, copy));
    } finally {
      // Navigation can retire downstream workspace effects while this scoped
      // component remains mounted. Always release its own pending dialog.
      setRestoring(null);
      if (!current()) setOpen(false);
    }
  }

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="ws-library-archive-open"
        onClick={() => {
          reset();
          setOpen(true);
        }}
      >
        {copy.archive}
      </Button>
      <Dialog
        open={open}
        onOpenChange={(value) => {
          if (!restoring) setOpen(value);
        }}
      >
        <DialogContent className="pc-portal ws-library-archive-dialog" showCloseButton={!restoring}>
          <DialogTitle>{copy.archive}</DialogTitle>
          <DialogDescription>{copy.archiveDescription}</DialogDescription>
          <Segmented<ArchiveKind>
            label={copy.archive}
            value={kind}
            disabled={restoring !== null}
            onChange={(value) => {
              if (!restoring) {
                reset();
                setKind(value);
              }
            }}
            options={[
              { value: 'file', label: copy.files },
              { value: 'result', label: copy.results },
            ]}
          />
          <div className="ws-library-archive-list" aria-busy={busy}>
            {items.map((item) => (
              <div className="ws-library-archive-item" key={item.id}>
                <div>
                  <strong>{item.title}</strong>
                  <p>
                    {formatCopy(copy.restoreUntil, {
                      date: new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(
                        new Date(item.restoreUntil),
                      ),
                    })}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => void restore(item.id)}
                >
                  {copy.restore}
                </Button>
              </div>
            ))}
            {!busy && !error && !items.length && <p className="ws-muted">{copy.empty}</p>}
            {loading && <p role="status">{panels.libraryLoading}</p>}
            {error && (
              <p className="ws-error" role="alert">
                {error}
              </p>
            )}
          </div>
          {error && (
            <Button variant="outline" disabled={busy} onClick={reset}>
              {panels.retry}
            </Button>
          )}
          {next !== null && !error && (
            <Button variant="outline" disabled={busy} onClick={() => setOffset(next)}>
              {copy.loadMore}
            </Button>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
